import React from 'react'
import PropTypes from 'prop-types'
import { FieldArray, Field, getIn } from 'formik'

import Radio from '../../Form/Radio/Radio'
import Checkbox from '../../Form/Checkbox/Checkbox'
import ModifierFieldset from '../ModifierFieldset/ModifierFieldset'
import { ModifierFieldLabel } from '../ModifierFieldLabel/ModifierFieldLabel'
import { useNestedModifiers } from '../NestedModifiersProvider/NestedModifiersProvider'
import { useFormik } from '../../FormikProvider/FormikProvider'
import { ModifierGroupShape } from '../NestedModifiersProvider/Models/ModifierGroup'

import './ModifierGroup.css'

export const ModifierGroup = ({ modifierGroup, getSelectedNames }) => {
  const { pushNestedModifier, recordCurrentSelections } = useNestedModifiers()
  const { values, setFieldValue, initialValues } = useFormik()

  const {
    name,
    modifiers,
    maxSelections,
    minSelections,
    pricingMode,
    guid: groupGuid
  } = modifierGroup

  const getModifierFieldComponent = (modifier) => {
    const onSelect = () => {
      if (!modifier.isSelected) {
        const currentValue = getIn(values, modifier.valueFieldName)
        const isSet = (typeof currentValue === 'boolean') || (typeof currentValue === 'string' && currentValue.length > 0)

        modifier.setSelected(setFieldValue,
          isSet ? currentValue : getIn(initialValues, modifier.valueFieldName))
      }

      const parentModifier = modifier.parent.parent

      parentModifier.formikValues = values
      recordCurrentSelections(modifier.parent.parent)

      pushNestedModifier(modifier)
    }

    const SelectorComponent = modifierGroup.isSingleSelect ? Radio : Checkbox
    const Component = (props) => <SelectorComponent {...props} onSelect={modifier.hasNested ? onSelect : undefined} />

    if (modifier.hasNested) {
      Component.displayName = `NestedModifier${modifierGroup.isSingleSelect ? 'Radio' : 'Checkbox'}Field`
    }

    return Component
  }

  return (
    modifiers.length > 0 && (
      <ModifierFieldset label={name} rules={{ minSelections, maxSelections, pricingMode }} name={`modifiers.${groupGuid}.value`}>
        <FieldArray
          name={groupGuid}
          render={() => (
            <div data-testid='modifier-group-body' styleName='modifiers'>
              {modifiers.map((modifier, index) => {
                const { itemGuid, price, name, hasNested } = modifier

                const formValue = values.modifiers[groupGuid].value
                const isSelected = modifierGroup.isSingleSelect ? formValue === itemGuid : formValue[index].selected
                const hasMaxSelections = !modifierGroup.isSingleSelect && (!isSelected && formValue.filter(i => i.selected).length === maxSelections)
                const isOutOfStock = modifier.outOfStock === true
                const isDisabled = hasMaxSelections || isOutOfStock
                const nestedModifiersValues = values.modifiers[groupGuid].detailsByItemGuid[itemGuid]
                const nestedModifiersNames = hasNested && isSelected ? getSelectedNames(nestedModifiersValues) : []

                return (
                  <Field
                    disabled={isDisabled}
                    key={itemGuid}
                    name={modifier.valueFieldName}
                    value={itemGuid}
                    component={getModifierFieldComponent(modifier)}
                    data-testid={itemGuid}
                    id={modifier.deepFieldNameByGuid}
                    label={(
                      <ModifierFieldLabel
                        name={name}
                        price={price}
                        hasNested={hasNested}
                        outOfStock={isOutOfStock}
                        nestedModifiersNames={nestedModifiersNames} />
                    )}
                  />
                )
              }
              )}
            </div>
          )}
        />
      </ModifierFieldset>
    )
  )
}

ModifierGroup.propTypes = {
  modifierGroup: ModifierGroupShape.isRequired,
  getSelectedNames: PropTypes.func.isRequired
}
